import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import { useToast } from "contexts/ToastContext";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingPageIcon } from "utils/constant";

import ConfirmationModal from "components/ConfirmationModal1";
import DocumentViewerModal from "components/DocumentViewer";
import OfficialFeedbackCard from "components/OfficialFeedbackCard";
import { StatusChip } from "components/StatusChip";
import { useAuth } from "contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { getStakeholder } from "services/getEmployees";
import {
  createLegalResearchRequest,
  getLegalResearchRequest,
  updateLegalResearchRequest,
} from "services/legalResearchService";
import { SingleLegalRequest } from "types";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { isTeamProsecutorOnly } from "./researchAccess";
import { formatDate, formatDay, calculateRemainingDays } from "utils/helpers";
import CoordinatorConductLegalResearch from "./coordinatorLegalResearch";
import DirectorLegalResearch from "./directorLegalReseach";
import RegistrarLegalResearch from "./fragments/RegistrarLegalResearch";
import HeadOfOAGBLegalRequest from "./headOfOAGBLegalRequest";
import ProsecutorLegalResearch from "./prosecutorLegalResearch/prosecutorLegalResearch";

const SingleConductLegalResearch = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const contextData = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [data, setData] = useState({
    id: "",
    isLoading: true,
    requested_by: "",
    requested_by_id: "",
    reason: "",
    status: "",
    forward_status: "",
    letter_reference_number: "",
    research_topic: "",
    research_topic_abstract: "",
    created_at: "",
    request_letter: null as File | null,
    draft_instruction: null as File | null,
    remark_for_stakeholder: "",
    research_completion_letter_file: "",
    messenger_name: "",
    documents: [],
    is_published: false,
    is_respondbacked_by_director: false,
    respondbackfeedbacks: [
      {
        id: "",
        file: null,
        file_details: null,
        given_by: "",
        role: "",
        note: "",
        feedback_type: "",
        status: "",
        created_at: "",
        updated_at: "",
        research_request: "",
      },
    ],
  });

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        let response = await getLegalResearchRequest(
          id || "",
          contextData.token || contextData?.user?.access_token || "",
        );

        let user = null;
        if (response.requested_by_id && response.requested_by !== "internal") {
          try {
            user = await getStakeholder(
              response.requested_by_id,
              contextData.token || contextData?.user?.access_token || "",
            );
          } catch (err) {
            console.error("Error fetching stakeholder:", err);
            showToast(t("FailedToFetchStakeholder"), "error");
          }
        }

        // Internal staff (director, coordinator, prosecutor, head, deputy,
        // management, etc.) can view every uploaded document. Only external
        // users get a limited set, collapsed to the latest file per type.
        let uniqueDocuments = response.documents;
        if (contextData?.user?.user_type === "external") {
          const filteredDocuments = response.documents?.filter(
            (doc: any) =>
              ![
                "proposal_file",
                "feedback_from_management_file",
                "research_draft_file",
                "research_draft_feedback",
              ].includes(doc.document_type),
          );
          uniqueDocuments = filteredDocuments;
          if (filteredDocuments) {
            const grouped = filteredDocuments.reduce((acc: any, doc: any) => {
              if (
                !acc[doc.document_type] ||
                new Date(doc.created_at) >
                  new Date(acc[doc.document_type].created_at)
              ) {
                acc[doc.document_type] = doc;
              }
              return acc;
            }, {});
            uniqueDocuments = Object.values(grouped);
          }
        }
        setData({
          isLoading: false,
          ...response,
          documents: uniqueDocuments,
          requested_by_user: user,
          is_published: response.is_published || false,
        });
      } catch (error) {
        console.error("Error fetching legal research request:", error);
        showToast(t("FailedToFetchLegalResearchRequest"), "error");
        setData((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    console.log("data: ", data);
  }, [data]);

  const handleBroadcast = async (isPublished: boolean) => {
    setBroadcastLoading(true);
    const updateData = new FormData();
    updateData.append("is_published", isPublished.toString());
    try {
      const response = await updateLegalResearchRequest(
        data.id,
        updateData,
        contextData.token || contextData.user.access_token || "",
      );
      setData((prev) => ({ ...prev, is_published: isPublished }));
      showToast(
        isPublished ? t("ResearchBroadcastedToPublic") : t("ResearchUnpublished"),
        "success",
      );
    } catch (error) {
      console.error("Error updating broadcast status:", error);
      showToast(t("FailedToUpdateBroadcastStatus"), "error");
    } finally {
      setBroadcastLoading(false);
    }
  };

  const [formState, setFormState] = useState<SingleLegalRequest>({
    reason: "",
    letter_reference_number: "",
    research_topic: "",
    research_topic_abstract: "",
    request_letter: null as File | null,
    draft_instruction: null as File | null,
    respondbackfeedbacks: null,
  });

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefDraftInstruction = useRef<HTMLInputElement>(null);

  const handleForwardStatus = async () => {
    setConfirmLoading(true);

    const updateData = new FormData();

    updateData.append("status", LEGAL_RESEARCH_REQUEST_STATUS.pending.value);
    updateData.append(
      "forward_status",
      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value,
    );

    try {
      const response = await updateLegalResearchRequest(
        data.id,
        updateData,
        contextData.token || contextData.user.access_token || "",
      );
      console.log(response);
      showToast(t("LegalResearchForwardedToDirector"), "success");
      setData(response);
      setOpenConfirmModal(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    for (const key in formState) {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      }
    }

    try {
      await createLegalResearchRequest(contextData.token, formData);
      showToast(t("LegalResearchRequestSentSuccess"), "success");
      setFormState({
        reason: "",
        letter_reference_number: "",
        research_topic_abstract: "",
        research_topic: "",
        request_letter: null,
        draft_instruction: null,
        respondbackfeedbacks: null,
      });
      if (fileInputRefRequestLetter.current)
        fileInputRefRequestLetter.current.value = "";
      if (fileInputRefDraftInstruction.current)
        fileInputRefDraftInstruction.current.value = "";
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToSendLegalResearchRequest"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (data.isLoading)
    return (
      <div className="flex justify-center items-center h-64">
        {LoadingPageIcon}
      </div>
    );

  if (
    contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
    contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") ||
    contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")
  )
    return <HeadOfOAGBLegalRequest request={data} />;

  if (contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector"))
    return <DirectorLegalResearch request={data} />;

  if (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator"))
    return <CoordinatorConductLegalResearch request={data} />;

  // Awareness / translation prosecutors put on a research team get the
  // prosecutor view too (isTeamProsecutorOnly uses expandRoles).
  if (
    contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") ||
    isTeamProsecutorOnly(contextData?.user?.roles || [])
  )
    return <ProsecutorLegalResearch request={data} />;
    
  // Registrar sees the document (left) panel — Proposal Document / Legal Research tabs —
  // but not the feedback/respondback (right) panel.
  const isRegistrar = contextData?.user?.roles?.some((p: string) => p?.toLowerCase().includes("registrar"));

  if (isRegistrar)
    return <HeadOfOAGBLegalRequest request={data} />;

  const req: any = data;
  const docBaseUrl = `${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`;
  const titleSchedule = req?.schedules?.find(
    (s: any) => s.purpose === "title_presentation",
  );
  const proposalSchedule = req?.schedules?.find(
    (s: any) => s.purpose === "proposal_presentation",
  );
  const draftSchedule = req?.schedules?.find(
    (s: any) => s.purpose === "draft_presentation",
  );
  const amendedDocs = (req?.documents ?? [])
    .filter(
      (d: any) =>
        d.document_type === "amended" ||
        d.document_type === "research_amended_file",
    )
    .slice()
    .sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  const completionDocs = (req?.documents ?? []).filter(
    (d: any) =>
      typeof d.document_type === "string" &&
      d.document_type.includes("completion"),
  );
  const synopsisDoc = (req?.documents ?? []).find(
    (d: any) =>
      d.document_type === "synopsis" ||
      d.document_type === "research_synopsis_file",
  );
  const letterDoc = (req?.documents ?? []).find(
    (d: any) =>
      d.document_type === "letter" ||
      d.document_type === "letter_file" ||
      d.document_type === "request_letter_file",
  );

  // Governmental schedule card (navy header + remaining-days badge + date/location).
  const ScheduleCard = ({ title, schedule }: { title: string; schedule: any }) => {
    if (!schedule) return null;
    const days = schedule?.scheduled_datetime
      ? calculateRemainingDays(schedule.scheduled_datetime)
      : null;
    const badge =
      days === null
        ? null
        : days < 0
          ? { label: t("OverdueByDays", { count: Math.abs(days) }), cls: "bg-red-100 text-red-700" }
          : days === 0
            ? { label: t("Today"), cls: "bg-orange-100 text-orange-700" }
            : { label: t("DaysLeft", { count: days }), cls: days <= 3 ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700" };
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
        <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
              </p>
              <h3 className="text-white font-bold text-base truncate">{title}</h3>
            </div>
          </div>
          {badge && (
            <span className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${badge.cls}`}>
              {badge.label}
            </span>
          )}
        </div>
        <div className="p-4 bg-white flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{t("Schedule") || "Date"}</p>
            {schedule?.scheduled_datetime ? (
              <p className="text-sm font-semibold text-[#0A1D39]">
                {formatDay(schedule.scheduled_datetime).weekday}, {formatDay(schedule.scheduled_datetime).rest}
              </p>
            ) : (
              <p className="text-sm text-gray-400">{t("DateNotScheduled")}</p>
            )}
          </div>
          {schedule?.location && (
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{t("Location")}</p>
              <p className="text-sm font-semibold text-[#0A1D39]">📍 {schedule.location}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Governmental document card (navy header + document viewer).
  const DocumentCard = ({ title, documents }: { title: string; documents: any[] }) => {
    if (!documents || documents.length === 0) return null;
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4">
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-[#0A1D39] flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#9B0505] font-semibold">
              {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
            </p>
            <h3 className="font-bold text-[#0A1D39] text-base truncate">{title}</h3>
          </div>
        </div>
        <DocumentViewerModal documents={documents} baseUrl={docBaseUrl} />
      </div>
    );
  };

  return (
    <div>
      <PageTitle
        icon="🏛️"
        text={t("OfficialLegalResearchRequestPortal")}
        subtitle={t("LegalResearchPortalSubtitle")}
        iconColor="text-red-800"
        textColor="text-gray-800"
      />
      <TwoColumnLayout
        left={
      <div>
        {/* Forward to Office of the Attorney General */}
        {(req.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value ||
          req.status === LEGAL_RESEARCH_REQUEST_STATUS.responded.value) && (
          <div className="flex">
            <button
              type="button"
              onClick={() => setOpenConfirmModal(true)}
              className="ml-auto bg-red-800 text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg hover:bg-red-900 transition-all duration-200 mb-4 border border-red-700"
            >
              {t("ForwardToOfficeOfAttorneyGeneral")}
            </button>
          </div>
        )}

        {/* Research abstract */}
        <Notes
          title={t("ResearchAbstract")}
          text={req?.research_topic_abstract ?? ""}
          subtext={formatDate(req.created_at ?? "")}
          topCorner={
            <div className="flex justify-end mb-4 gap-2">
              <StatusChip status={req?.status || ""} />
              {(req.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value ||
                req.status === LEGAL_RESEARCH_REQUEST_STATUS.responded.value ||
                req?.is_respondbacked_by_director) && (
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      navigate("/conduct-legal-research/request", {
                        state: { request: req },
                      })
                    }
                    type="button"
                    className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                  >
                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          }
        />
        <br />

        {/* Head of OAGB response feedback */}
        {req?.remark_for_stakeholder && (
          <>
            <Notes
              title={t("HeadOfOAGBResponseFeedback")}
              text={req?.remark_for_stakeholder ?? ""}
            />
            <br />
          </>
        )}

        {/* Title presentation schedule */}
        <ScheduleCard title={t("TitlePresentation") || "Title Presentation"} schedule={titleSchedule} />

        {/* Proposal presentation schedule */}
        <ScheduleCard title={t("ProposalPresentation") || "Proposal Presentation"} schedule={proposalSchedule} />

        {/* Research (draft) presentation schedule */}
        <ScheduleCard title={t("ResearchDraftPresentation") || "Research Presentation"} schedule={draftSchedule} />

        {/* Latest amended research file */}
        {amendedDocs.length > 0 && (
          <DocumentCard title={t("ResearchAmendedFile")} documents={[amendedDocs[0]]} />
        )}

        {/* Completion letter file */}
        {completionDocs.length > 0 ? (
          <DocumentCard title={t("ResearchCompletionLetterFile")} documents={completionDocs} />
        ) : req?.research_completion_letter_file ? (
          <DocumentCard
            title={t("ResearchCompletionLetterFile")}
            documents={[
              {
                document_path: req.research_completion_letter_file,
                document_type: "research_completion_letter_file",
                uu_id: "1",
                document_uuid: "1",
                created_at: req.created_at,
                updated_at: req.created_at,
              },
            ]}
          />
        ) : null}

        {/* First synopsis */}
        {synopsisDoc && (
          <DocumentCard title={t("ResearchSynopsis")} documents={[synopsisDoc]} />
        )}

        {/* Letter file */}
        {letterDoc && (
          <DocumentCard title={t("Letter")} documents={[letterDoc]} />
        )}
      </div>
        }
        right={
          <div className="w-full max-w-md space-y-4">
            <OfficialFeedbackCard
              feedbacks={req.respondbackfeedbacks}
              feedbackType="stakeholder_request"
            />

            <div className="bg-white rounded-lg shadow-md p-6 space-y-4 border border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-lg text-gray-800">
                    {t("RelatedServices")}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {t("RelatedServicesDescription")}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-300 pt-4 space-y-3">
                <div className="flex justify-between items-center p-3 hover:bg-red-50 rounded-md cursor-pointer transition-colors">
                  <span className="text-sm font-medium text-gray-700">
                    {t("ConductingLegalResearch")}
                  </span>
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div className="flex justify-between items-center p-3 hover:bg-red-50 rounded-md cursor-pointer transition-colors">
                  <span className="text-sm font-medium text-gray-700">
                    {t("LegislativeDrafting")}
                  </span>
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        }
      />

      <ConfirmationModal
        isOpen={openConfirmModal}
        title={t("ConfirmAction")}
        confirmText={t("YesContinue")}
        cancelText={t("Cancel")}
        isLoading={confirmLoading}
        onCancel={() => setOpenConfirmModal(false)}
        onConfirm={() => handleForwardStatus()}
        message={t("ConfirmForwardToAttorneyGeneralMessage")}
      />
    </div>
  );
};

export default SingleConductLegalResearch;
